import { Module } from 'vuex';

import router from '@/router';
import { Control } from '@/services/controls/controls.types';
import { devicesService } from '@/services/devices/devices.service';
import { Device, RelocateOptions } from '@/services/devices/devices.types';
import { dicoLabelsService } from '@/services/dico-labels/dico-labels.service';
import { formsService } from '@/services/forms/forms.service';
import { RootState } from '@/store';

import { AppSectionCode } from '@/services/app-sections/app-sections.types';
import { Grade } from '@/services/grades/grades.types';
import { gradesService } from '@/services/grades/grades.service';
import { reportsService } from '@/services/reports/reports.service';
import { Report } from '@/services/reports/reports.types';
import { PurchaseItemCalcul } from '@/services/purchases/purchases.types';

export interface DeviceState {
  device: Device;
  deviceLoading: boolean;
  controlLabelLoading: boolean;
  controlReports: Report[];
  grades: Grade[];
}

const state: DeviceState = {
  device: null,
  deviceLoading: false,
  controlLabelLoading: false,
  controlReports: [],
  grades: []
};

const options: Module<DeviceState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    setDevice: ({ commit, dispatch }, device: Device | string | number): Promise<void> => {
      if (typeof device === 'number' || typeof device === 'string') return dispatch('getDeviceBySerialNo', device);
      else commit('device', device);
    },
    newDevice: ({ commit, dispatch }): Promise<void> => {
      commit('device', null);
      commit('deviceLoading', true);
      return devicesService
        .newDevice()
        .then(device => device && commit('device', device))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    createDevice: ({ commit, dispatch }, device: Device): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .create(device)
        .then(device => device && commit('device', device))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    getDeviceBySerialNo: ({ commit, dispatch }, serialNo: string | number): Promise<void> => {
      commit('deviceLoading', true);

      return devicesService
        .get(serialNo)
        .then(device => device && commit('device', device))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    findDeviceBySerialNo: (
      { commit, dispatch },
      options: { serialNo: string; stocks?: AppSectionCode[] }
    ): Promise<void> => {
      commit('deviceLoading', true);
      const { serialNo, stocks } = options;

      return devicesService
        .findBySerialNo(serialNo, { stocks })
        .then(device => device && typeof device === 'object' && dispatch('setDevice', device))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    checkControlStatus: (
      { commit, dispatch },
      { imei, serialNo, selectedReference }: { imei: string; serialNo: string; selectedReference?: string }
    ): Promise<void> => {
      commit('setDeviceLoading', true);
      return devicesService
        .checkDeviceControl(imei, serialNo, selectedReference)
        .then(device => {
          if (device) {
            commit('setDevice', device);
          } else {
            dispatch('alert/pushError', 'Device not found or not under control', { root: true });
          }
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('setDeviceLoading', false));
    },
    getDeviceFromControl: ({ commit, dispatch }, control: Control): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .exist(control.serial_no)
        .then(exists => {
          if (exists) return devicesService.get(control.serial_no);
          return null;
        })
        .then(device => {
          if (device) {
            // Merge device + control but keep the device values for supplier/price fields
            // (they must not be overridden by the control spread).
            commit('device', {
              ...device,
              ...control,
              initial_comment: device.initial_comment,
              created_at: device.created_at,
              updated_at: device.updated_at,
              supplier_reference: device.supplier_reference,
              supplier_id: device.supplier_id,
              purchase_price: device.purchase_price
            });
          } else commit('device', control);
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
        })
        .finally(() => commit('deviceLoading', false));
    },
    saveReport: ({ commit, dispatch }, control_report_id: number): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .downloadReport(control_report_id)
        .then(report => report && dispatch('file/saveBase64As', report, { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    generateNewReport: ({ commit, dispatch }, { serial_no, step }): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .getReport(serial_no, step)
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    sellableOperation: ({ commit, dispatch }, opt: { serial_no: string; type: string }): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .sellableOperation(opt.serial_no, opt.type)
        .then(device => {
          device && commit('device', device);
          dispatch('alert/pushSuccess', 'Ordinateur mis à jour !', { root: true });
          if (device?.id) {
            return dispatch('flowManagement', { deviceId: device.id, step: 'control' }).then(() => undefined);
          }
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    initDevice: async (
      { commit, dispatch },
      {
        control,
        device,
        linkedPurchase,
        mpBuybackId
      }: {
        control: Control;
        device: Device;
        linkedPurchase: PurchaseItemCalcul;
        mpBuybackId?: string;
      }
    ): Promise<Device> => {
      commit('deviceLoading', true);
      return devicesService
        .createFromControl(control.id, { ...control, ...device, control_id: control.id }, linkedPurchase, mpBuybackId)
        .then(device => {
          if (device) {
            commit('device', device);

            return device;
          } else {
            throw new Error('Device is undefined after creation');
          }
        })
        .then(() => dispatch('alert/pushSuccess', 'Contrôle validé !', { root: true }))
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          throw error;
        })
        .finally(() => commit('deviceLoading', false));
    },
    updateDevice: ({ commit, state, dispatch }, data: Device): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .update(state.device.serial_no, data)
        .then(device => device && commit('device', device))
        .then(() => dispatch('alert/pushSuccess', 'Ordinateur mis à jour !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    deleteDevice: ({ commit, dispatch }, serialNo: string): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .delete(serialNo)
        .then(() => commit('device', null))
        .then(() => dispatch('alert/pushSuccess', 'Ordinateur supprimé !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    flowManagement: (
      { commit, dispatch },
      opt: { deviceId: number; step: string; params?: string[] | null }
    ): Promise<boolean> => {
      commit('deviceLoading', true);
      return devicesService
        .flowManagement(opt.deviceId, opt.step, opt.params)
        .then(rslt => {
          commit('device', rslt.device);
          if (rslt.move) {
            dispatch('alert/pushSuccess', 'Ordinateur deplace : ' + rslt.stock_status.display_name, { root: true });
            commit('deviceLoading', false);
            return true;
          } else if (rslt.stock_status) {
            // Release loading before opening the modal so its action buttons stay clickable
            commit('deviceLoading', false);
            // Do not show the relocate confirmation modal on diagnosis / battery / repair / install / cleaning / packing pages
            // Unless the backend explicitly forces the modal (e.g. regrading needed after repair)
            const currentRouteName = String(router.currentRoute.value.name || '');
            const skipRelocateModal = /^(diagnosis|battery|repair|install|clean_control|clean_prod|packing)(-|$)/.test(
              currentRouteName
            );
            if (skipRelocateModal && !rslt.force_modal) {
              return true;
            }
            // Wait for the relocate modal to be closed; resolve true only when user confirmed
            return new Promise<boolean>(resolve => {
              dispatch(
                'modal/showModal',
                {
                  component: 'ConfirmRelocateModal',
                  data: { device: rslt.device, stockStatus: rslt.stock_status, fromFlowManagement: true },
                  onClose: (value: unknown) => resolve(!!value)
                },
                { root: true }
              );
            });
          }
          commit('deviceLoading', false);
          return false;
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          commit('deviceLoading', false);
          return false;
        });
    },
    completeCleaning: (
      { commit, dispatch },
      { deviceId, machineType }: { deviceId: number; machineType: string }
    ): Promise<boolean> => {
      commit('deviceLoading', true);
      return devicesService
        .completeCleaning(deviceId, machineType)
        .then(rslt => {
          commit('device', rslt.device);
          commit('deviceLoading', false);
          if (rslt.move) {
            dispatch('alert/pushSuccess', 'Nettoyage terminé : ' + rslt.stock_status.display_name, { root: true });
          }
          return true;
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          commit('deviceLoading', false);
          return false;
        });
    },
    putInStock: async (
      { commit, state, dispatch },
      { stockId, reason, serialNo }: { stockId: number; reason: string; serialNo?: string }
    ): Promise<void> => {
      commit('deviceLoading', true);
      const targetSerialNo = serialNo || state.device?.serial_no;
      if (!targetSerialNo) {
        dispatch(
          'alert/pushError',
          { message: "Impossible de déplacer l'ordinateur: numéro de série manquant." },
          { root: true }
        );
        commit('deviceLoading', false);
        return Promise.resolve();
      }
      return devicesService
        .move(targetSerialNo, stockId, reason)
        .then(device => {
          commit('device', device);
        })
        .then(() => dispatch('alert/pushSuccess', "L'ordinateur a bien été déplacé !", { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    relocateDevice: ({ dispatch }, options: RelocateOptions): Promise<any> => {
      const { stockId, reason, serialNo } = options;
      return dispatch('putInStock', { stockId, reason, serialNo });
    },
    printControlLabel: ({ commit, dispatch }, serialNo: string): Promise<void> => {
      commit('controlLabelLoading', true);
      return devicesService
        .getLabel(serialNo)
        .then(zpl => zpl && dispatch('printer/printWith', { data: [zpl], printer: 'CONTROL' }, { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('controlLabelLoading', false));
    },
    printMiniLabel: ({ commit, dispatch }, serialNo: string): Promise<void> => {
      commit('controlLabelLoading', true);
      return devicesService
        .getMiniLabel(serialNo)
        .then(zpl => zpl && dispatch('printer/printWith', { data: [zpl], printer: 'MINI' }, { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('controlLabelLoading', false));
    },
    clearDevice: ({ dispatch }): Promise<void> => {
      return dispatch('setDevice', null);
    },
    importUpdateStatus: ({ commit, dispatch }, { file }: { file: File }): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .importUpdateStatus(file)
        .then(resp =>
          dispatch(
            'alert/pushSuccess',
            `${resp.count} modification${resp.count > 1 ? 's' : ''} importée${resp.count > 1 ? 's' : ''} !`,
            { root: true }
          )
        )
        .catch(error => {
          const errorData = error.response?.data;
          let errorMessage = error.message || "Erreur lors de l'import";

          if (errorData?.errors?.length > 0) {
            const firstTenErrors = errorData.errors.slice(0, 10);
            const errorCount = errorData.errors.length;
            const moreCount = errorCount > 10 ? errorCount - 10 : 0;
            const errorsList = firstTenErrors.join('\n');
            errorMessage = `${errorsList}${moreCount > 0 ? `\n\n... et ${moreCount} autres erreurs` : ''}`;
          }

          if (errorData?.not_found?.length > 0) {
            const notFoundCount = errorData.not_found.length;
            errorMessage += `\n\n${notFoundCount} ordinateur${notFoundCount > 1 ? 's' : ''} non trouvé${
              notFoundCount > 1 ? 's' : ''
            }`;
          }

          dispatch('alert/pushAlert', { type: 'error', text: errorMessage, duration: 8 }, { root: true });
        })
        .finally(() => commit('deviceLoading', false));
    },
    generateTasks: ({ commit, dispatch }, opt: { serialNo: string; partsCode: string[] }): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .generateTasks(opt.serialNo, opt.partsCode)
        .then(device => device && commit('device', device))
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    generateAestheticTasks: ({ commit, dispatch }, serialNo: string): Promise<void> => {
      commit('deviceLoading', true);
      return devicesService
        .generateAestheticTasks(serialNo)
        .then(device => device && commit('device', device))
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    getDeviceControlReports: ({ commit, dispatch }, serial_no: string): Promise<void> => {
      commit('deviceLoading', true);
      return reportsService
        .list(serial_no)
        .then(control_reports => control_reports && commit('controlReports', control_reports))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    },
    getDeviceGrades: ({ commit, dispatch }, serial_no: string): Promise<void> => {
      commit('deviceLoading', true);
      return gradesService
        .list(serial_no)
        .then(grades => grades && commit('grades', grades))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('deviceLoading', false));
    }
  },
  mutations: {
    device: (state, device) => (state.device = device),
    deviceLoading: (state, loading) => (state.deviceLoading = loading),
    controlLabelLoading: (state, loading) => (state.controlLabelLoading = loading),
    controlReports: (state, loading) => (state.controlReports = loading),
    grades: (state, loading) => (state.grades = loading)
  },
  getters: {
    controlForm: (state, getters, rootState) => {
      if (!rootState['global-settings'].settings) return null;
      return formsService.getForm('control', state.device, rootState['global-settings'].settings);
    },
    controlRelocateForm: (state, getters, rootState) => {
      if (!rootState['global-settings'].settings) return null;
      return formsService.getForm('control-relocate', state.device, rootState['global-settings'].settings);
    },
    deviceCreationForm: (state, getters, rootState) => {
      if (!rootState['global-settings'].settings) return null;
      return formsService.getForm('device-creation', state.device, rootState['global-settings'].settings);
    },
    deviceForm: (state, getters, rootState) => {
      if (!rootState['global-settings'].settings) return null;
      return formsService.getForm('device', state.device, rootState['global-settings'].settings);
    },
    operatorName: state => dicoLabelsService.formatName(state.device?.operator),
    bookedByName: state => dicoLabelsService.formatName(state.device?.booked_by)
  }
};

export default options;
